import mergeSort, { stringCompare } from '../../../Utils/mergeSort';
import { clone, take } from 'ramda';
import { findValue } from '../../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/ObjectTable/ObjectTableRow';
import { safeGetAttributeInfo } from '../../../Model/avenyModelUtils';
import { collectionMap } from '../../../Model/ModelContext/ModelContext';

/** Temp tills nya dialog sorteringen introduceras */

export type ISortOrder = 'asc' | 'desc' | undefined;

export interface ISortProperty {
  caption: string;
  orderBy: string;
  order: ISortOrder;
}

export interface ISortInfo<T> {
  rows: T[];
  properties: ISortProperty[];
  filtered?: boolean;
}

export const sortRows = <T>(
  sortProperties: ISortProperty[],
  rows: T[],
  getValue: (row: T, columnName: string) => string | number | null,
  compareFunction: (
    a: string | number,
    b: string | number,
    order: ISortOrder
  ) => number
): T[] => {
  if (!sortProperties || sortProperties.length === 0) {
    return rows;
  }

  return mergeSort(rows, (a, b) =>
    compare(a, b, clone(sortProperties), getValue, compareFunction)
  );
};

const compare = <T>(
  a: T,
  b: T,
  sortProperties: ISortProperty[],
  getValue: (row: T, columnName: string) => string | number | null,
  compareFunction: (
    a: string | number,
    b: string | number,
    order: ISortOrder
  ) => number
): number => {
  if (sortProperties.length === 0) {
    return 0;
  }
  const property = sortProperties[0];
  const aCell = getValue(a, property.orderBy);
  const bCell = getValue(b, property.orderBy);
  if (aCell == null && bCell == null) {
    return 0;
  }
  if (aCell == null) {
    return 1;
  }
  if (bCell == null) {
    return -1;
  }
  const trimmedProperties =
    sortProperties.length > 1 ? sortProperties.slice(1) : [];
  if (cellIsString(aCell) || cellIsString(bCell)) {
    const comparison = compareFunction(aCell, bCell, property.order);

    return comparison === 0
      ? compare(a, b, trimmedProperties, getValue, compareFunction)
      : comparison;
  }

  const comparison = compareFunction(
    Number.parseFloat(aCell as string),
    Number.parseFloat(bCell as string),
    property.order
  );

  return comparison === 0
    ? compare(a, b, trimmedProperties, getValue, compareFunction)
    : comparison;
};

export const numberOrStringCompare = (
  a: number | string,
  b: number | string,
  order: ISortOrder
) => {
  if (order === 'asc') {
    if (a === b) {
      return 0;
    }

    if (typeof a === 'string' && typeof b === 'string') {
      return stringCompare(a, b);
    }
    return a > b ? 1 : -1;
  }
  if (order === 'desc') {
    if (a === b) {
      return 0;
    }

    if (typeof a === 'string' && typeof b === 'string') {
      const res = stringCompare(a, b);
      if (res === 0) {
        return 0;
      }
      return res === -1 ? 1 : -1;
    }
    return a > b ? -1 : 1;
  }

  return 0;
};

const cellIsString = (cell: number | string) => {
  return cell === '' || Number.isNaN(+cell);
};

export const createSortHandler =
  <T>(sortInfo: ISortInfo<T>, updateSortInfo: (info: ISortInfo<T>) => void) =>
  (orderBy: string, caption: string) =>
  () => {
    let newProperties = clone(sortInfo.properties);
    const property: ISortProperty | undefined = newProperties.find(
      x => x.orderBy === orderBy
    );
    if (property) {
      const index = newProperties.indexOf(property);
      if (index === 0) {
        switch (property.order) {
          case 'asc':
            property.order = 'desc';
            break;
          case 'desc':
            newProperties.splice(index, 1);
            break;
          case undefined:
            property.order = 'asc';
            break;
        }
      } else {
        newProperties.splice(index, 1);
        newProperties.unshift(property);
      }
    } else {
      newProperties.unshift({ caption: caption, orderBy, order: 'asc' });
    }
    updateSortInfo({
      rows: sortInfo.rows,
      properties: take(3, newProperties),
    });
  };

export const standardRutinCompare = (
  a: number | string,
  b: number | string,
  order: ISortOrder
) => {
  return numberOrStringCompare(a, b, order);
};

export const standardRutinGetSortValue =
  (collectionName: string) =>
  (
    a: {
      id: string;
      _version: number;
    },
    columnName: string
  ): string | number | null => {
    if (!a) {
      return null;
    }

    const attribute = safeGetAttributeInfo(
      collectionName,
      columnName,
      collectionMap
    );
    const enumSortValues = attribute.isOk()
      ? attribute.value.attribute.EnumSortValues
      : undefined;

    const value = findValue(a, columnName.split('.')) || '';

    const sortValue =
      value && typeof value === 'string' && enumSortValues
        ? enumSortValues.get(value)
        : value;

    return sortValue ?? null;
  };
